import type { ImgHTMLAttributes } from 'react';
import { motion } from 'motion/react';

import { IMAGE_METADATA } from '@/data/imageMetadata';

export type OptimizedImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'alt' | 'src'> & {
  src: string;
  alt: string;
  animated?: boolean;
};

const normalizeImagePath = (src: string) => {
  if (src.startsWith('/') || /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(src)) {
    return src;
  }
  return `/${src}`;
};

const OptimizedImage = ({
  src,
  alt,
  animated = false,
  className,
  width,
  height,
  loading = 'lazy',
  decoding = 'async',
  ...imageProps
}: OptimizedImageProps) => {
  const normalizedSrc = normalizeImagePath(src);
  const metadata = IMAGE_METADATA[normalizedSrc];
  const imagePropsWithDefaults = {
    ...imageProps,
    src: metadata?.src ?? normalizedSrc,
    alt,
    className,
    width: width ?? metadata?.width,
    height: height ?? metadata?.height,
    loading,
    decoding,
  };

  const pictureContent = (
    <>
      {metadata?.webp && <source srcSet={metadata.webp} type="image/webp" />}
      <img {...imagePropsWithDefaults} />
    </>
  );

  return animated ? (
    <motion.picture
      className="block"
      initial={{ opacity: 0, scale: 0.98 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, amount: 0.3 }}
    >
      {pictureContent}
    </motion.picture>
  ) : (
    <picture className="block">{pictureContent}</picture>
  );
};

export default OptimizedImage;
